/**
 * The browser's effects behind the shared Set type sequence (`setPageType` in
 * collab-client), which decides everything; this module only says how each
 * step is done in a browser:
 *
 * - the page's text is read from its DocumentRoom (after any open editor of it
 *   has flushed), and read again before the page goes, to catch a late edit;
 * - the item is created through the project's tracker room, which writes the
 *   body room first, and counts as reaching the team only once the room has
 *   acknowledged it;
 * - the item's body is read back from `tracker-content/<id>` and compared;
 * - placement, child moves and trash go through the docs session.
 *
 * An item the room has not acknowledged in time may still have reached the
 * team, so it is never deleted: its read-back reports it unconfirmed, which
 * keeps both. A child page or type moves only on a server that confirms page
 * moves; elsewhere the page is kept rather than trashed over unconfirmed moves.
 * The page is untouched until the read-back passes.
 */
import type { CollabDocsSession, loadSetPageType } from '@nimbalyst/collab-bundle/docs-ui';

type PageTypes = Awaited<ReturnType<typeof loadSetPageType>>;
type Dependencies = Parameters<PageTypes['setPageType']>[1];
type ItemInput = ReturnType<PageTypes['browserPageTypeItemInput']>;
type ChildMoveSession = Parameters<PageTypes['listPageChildren']>[0];
type TypePlacements = Parameters<PageTypes['listPageChildren']>[1];

/** The tracker room commands Set type sends. */
export type SetTypeTrackerCommand =
  | { type: 'create-item'; item: ItemInput }
  | { type: 'delete-item'; itemId: string };

export interface WebSetPageTypeContext {
  workspace: string;
  session: ChildMoveSession & Pick<CollabDocsSession, 'trashDocument'> & {
    /** The browser data source says whether the server confirms page moves; absent means it does not. */
    dataSource: { echoesAuthorWrites?: () => boolean };
  };
  typePlacements: () => TypePlacements;
  pageTypes: Pick<PageTypes, 'listPageChildren' | 'movePageChild'>;
  /** The item to create; `browserPageTypeItemInput` from the same module. */
  itemInput: (request: { typeId: string; title: string; markdown: string; workspace: string }) => ItemInput;
  /** A team document room's text as markdown. Throws when it cannot be read. */
  readDocumentMarkdown: (documentId: string) => Promise<string>;
  trackerCommand: (command: SetTypeTrackerCommand) => Promise<unknown>;
  /** Whether the tracker room has acknowledged this item. */
  itemSynced: (itemId: string) => boolean;
  subscribeItems: (listener: () => void) => () => void;
  /** Saves edits an open editor of the page still holds; throws if it cannot. */
  flushPageEditor: (pageId: string) => Promise<void>;
  openItem: (itemId: string) => void;
  wait: (ms: number) => Promise<void>;
  publishTimeoutMs?: number;
}

const PUBLISH_TIMEOUT_MS = 10_000;

function waitUntilSynced(context: WebSetPageTypeContext, itemId: string): Promise<boolean> {
  if (context.itemSynced(itemId)) return Promise.resolve(true);
  return new Promise((resolve) => {
    const finish = (synced: boolean) => {
      clearTimeout(timer);
      unsubscribe();
      resolve(synced);
    };
    const timer = setTimeout(() => finish(context.itemSynced(itemId)), context.publishTimeoutMs ?? PUBLISH_TIMEOUT_MS);
    const unsubscribe = context.subscribeItems(() => {
      if (context.itemSynced(itemId)) finish(true);
    });
  });
}

const sameText = (left: string, right: string) => left.trimEnd() === right.trimEnd();

async function readPage(context: WebSetPageTypeContext, pageId: string) {
  await context.flushPageEditor(pageId);
  return { markdown: await context.readDocumentMarkdown(pageId) };
}

export function buildWebSetPageTypeDependencies(context: WebSetPageTypeContext): Dependencies {
  const { session, pageTypes } = context;
  /** Created items the room has not acknowledged yet. */
  const unconfirmed = new Set<string>();
  return {
    listChildren: (pageId) => pageTypes.listPageChildren(session, context.typePlacements(), pageId),
    moveChildUnderItem: async (child, itemId) => {
      // A page or type moves as a page move; a typed page's placement is always echoed.
      if (child.kind !== 'item' && session.dataSource.echoesAuthorWrites?.() !== true) {
        return { ok: false, error: 'the team server cannot confirm page moves yet' };
      }
      return pageTypes.movePageChild(session, context.typePlacements(), child, itemId);
    },
    flushPageEditor: (pageId) => context.flushPageEditor(pageId),
    readPageMarkdown: (pageId) => context.readDocumentMarkdown(pageId).then((markdown) => ({ markdown })),
    createItem: async ({ typeId, title, markdown }) => {
      const item = context.itemInput({ typeId, title, markdown, workspace: context.workspace });
      await context.trackerCommand({ type: 'create-item', item });
      // Unacknowledged is not unpublished: teammates may already be editing it,
      // so it is treated as published and never removed.
      if (!(await waitUntilSynced(context, item.id))) unconfirmed.add(item.id);
      return { itemId: item.id, publication: 'published' };
    },
    verifyItemBody: async (itemId, markdown) => {
      // Re-checked on each read-back attempt, so a late acknowledgement carries on.
      if (unconfirmed.has(itemId)) {
        if (!context.itemSynced(itemId)) {
          return { status: 'unreadable', reason: 'the tracker room did not confirm the item, which may already be visible to the team' };
        }
        unconfirmed.delete(itemId);
      }
      const body = await context.readDocumentMarkdown(`tracker-content/${itemId}`);
      return sameText(body, markdown) ? { status: 'match' } : { status: 'mismatch' };
    },
    removeItem: async (itemId) => {
      await context.trackerCommand({ type: 'delete-item', itemId });
    },
    setItemPlacement: async (itemId, parentId, position) => {
      const existing = session.getItemPlacements().find((placement) => placement.itemId === itemId);
      const result = await session.setItemPlacement(
        itemId,
        parentId,
        position.sortOrder ?? existing?.sortOrder ?? undefined,
        parentId ? position.parentKind : undefined,
      );
      return result.ok ? { ok: true } : { ok: false, error: result.error || 'the placement was refused' };
    },
    pageUnchangedSince: async (pageId, copy) => sameText((await readPage(context, pageId)).markdown, copy.markdown),
    trashPage: async (pageId) => {
      const result = await session.trashDocument(pageId);
      // A refusal must reach the sequence, which then keeps both and says so.
      if (!result.ok) throw new Error(result.error);
    },
    openItem: (itemId) => context.openItem(itemId),
    wait: (ms) => context.wait(ms),
  };
}
